import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiTrendingUp,
  FiAlertTriangle,
  FiPlus,
} from "react-icons/fi";
import { appActions } from "../../../redux/actions/in-app";
import { Customer, Order, getStatusLabel } from "./utils/customerTypes";
import UpdateCustomerModal from "../../../shared/components/modals/UpdateCustomerModal";
import SuspendAccountModal from "../../../shared/components/modals/SuspendAccountModal";
import ActivateAccountModal from "../../../shared/components/modals/ActivateAccountModal";
import Modal from "react-bootstrap/Modal";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { getDateFromISO } from "../../../shared/utils";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { LoadingItem } from "../../../shared/components/loading";
import MasterCard from "../../../assets/logos_mastercard.svg";
import VisaCard from "../../../assets/visa.svg";
import StatusCardIcon from "../../../assets/status_card_icon.svg";
import ContactCardIcon from "../../../assets/contact_card_icon.svg";
import AddressCardIcon from "../../../assets/address_card_icon.svg";
import AmountCardIcon from "../../../assets/amount_card_icon.svg";
import CustomPagination from "../../../shared/components/pagination/CustomPagination";
import "./CustomerDetails.scss";

interface CustomerDetailsProps {
  getCustomerDetails: typeof appActions.GetCustomerDetails;
  getCustomerOrders: typeof appActions.GetCustomerOrders;
  getCustomerAddress: typeof appActions.GetCustomerAddress;
  getCustomerTotalSpent: typeof appActions.GetCustomerTotalSpent;
  toggleUser: typeof appActions.ToggleUser;
  customerDetails: any;
  customerOrders: any;
  customerAddress: any;
  customerTotalSpent: any;
  getOrderDetails: typeof appActions.GetOrderDetails;
  getOrderDetailsRequest: any;
  updateCustomer: typeof appActions.UpdateCustomer;
  updateCustomerRequest: any;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  getCustomerDetails,
  getCustomerOrders,
  getCustomerAddress,
  getCustomerTotalSpent,
  toggleUser,
  customerDetails,
  customerOrders,
  customerAddress,
  customerTotalSpent,
  getOrderDetails,
  getOrderDetailsRequest,
  updateCustomer,
  updateCustomerRequest,
}) => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Stable refs to action creators to avoid stale closures
  const getCustomerDetailsRef = useRef(getCustomerDetails);
  const getCustomerOrdersRef = useRef(getCustomerOrders);
  getCustomerDetailsRef.current = getCustomerDetails;
  getCustomerOrdersRef.current = getCustomerOrders;

  const fetchInitialData = React.useCallback(
    async (silent: boolean = false) => {
      if (!silent) setInitialLoading(true);
      try {
        await getCustomerDetailsRef.current(customerId!);
        await getCustomerOrdersRef.current(customerId!, 1, pageSize);
      } catch (error) {
        console.error("Error fetching initial customer data:", error);
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    // Only re-create when customerId or pageSize changes, NOT on action creator re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, pageSize],
  );

  useEffect(() => {
    if (customerId) {
      fetchInitialData();
    }
  }, [customerId, fetchInitialData]);

  useEffect(() => {
    if (customerId) {
      getCustomerTotalSpent(customerId);
    }
  }, [customerId, getCustomerTotalSpent]);

  useEffect(() => {
    if (customerId && !initialLoading) {
      getCustomerOrdersRef.current(customerId, currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, currentPage, pageSize]);

  const customer: Customer | null =
    customerDetails?.data?.data || customerDetails?.data;
  const orders: Order[] =
    customerOrders?.data?.data?.results || customerOrders?.data?.results || [];
  const customerAddressData =
    customerAddress?.request_data?.response?.data?.data?.results?.[0] ||
    customerAddress?.request_data?.response?.data?.results?.[0] ||
    customerAddress?.request_data?.response?.results?.[0] ||
    customerAddress?.request_data?.response?.data?.data ||
    customerAddress?.request_data?.response?.data ||
    customerAddress?.request_data?.response;

  const paginationData = customerOrders?.data?.data || customerOrders?.data;
  const totalPages =
    paginationData?.totalNumberOfPages ||
    Math.ceil((paginationData?.totalNumberOfRecords || 0) / pageSize);

  useEffect(() => {
    if (customerId) {
      getCustomerAddress(`?UserId=${customerId}`);
    }
  }, [customerId, getCustomerAddress]);

  const handleBack = () => {
    navigate("/app/customers");
  };

  const handleEditCustomer = () => {
    updateCustomer("", "CLEAR"); // Reset update status
    setShowEditModal(true);
  };

  const handleSuspendAccount = () => {
    if (customer) {
      setShowSuspendModal(true);
    }
  };

  const handleActivateAccount = () => {
    if (customer) {
      setShowActivateModal(true);
    }
  };

  const handleConfirmSuspend = (reason: string) => {
    toggleUser(`?Id=${customerId}`);
    setShowSuspendModal(false);
  };

  const handleConfirmActivate = () => {
    toggleUser(`?Id=${customerId}`);
    setShowActivateModal(false);
  };

  const getOrderInfo = async (orderNum: any) => {
    await getOrderDetails(`?Number=${orderNum}`);
  };

  const hideOrderModal = () => {
    getOrderDetails("CLEAR");
  };

  const handleUpdateCustomer = async (data: any) => {
    try {
      await updateCustomer(customerId!, data);
    } catch (error) {
      console.error("Error updating customer:", error);
    }
  };

  const handleModalHide = React.useCallback(() => {
    const wasSuccessful = updateCustomerRequest?.success;
    setShowEditModal(false);
    // CRITICAL: clear the success status immediately so it can't retrigger
    updateCustomer("", "CLEAR");
    if (wasSuccessful) {
      fetchInitialData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCustomerRequest?.success]);

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const returnDeliveryStatus = (status: string | number) => {
    let orderStatusList = [
      { statusName: "Initiated", orderStatus: 1, amber: "cancel" },
      { statusName: "Processing", orderStatus: 2, amber: "pending" },
      { statusName: "Confirmed", orderStatus: 3, amber: "blue" },
      { statusName: "Failed", orderStatus: 4, amber: "cancel" },
      { statusName: "Ready For Pickup", orderStatus: 5, amber: "light_gr" },
      { statusName: "Delivery Started", orderStatus: 6, amber: "dark_or" },
      { statusName: "Delivered", orderStatus: 7, amber: "success" },
      { statusName: "Delivery Failed", orderStatus: 8, amber: "cancel" },
      { statusName: "Payment Failed", orderStatus: 9, amber: "cancel" },
      { statusName: "Pending Confirmation", orderStatus: 10, amber: "pending" },
      { statusName: "Cancelled", orderStatus: 11, amber: "cancel" },
    ];

    let orderStatus = orderStatusList.find(
      (each: any) => each?.orderStatus === status,
    );
    if (!orderStatus && typeof status === "string") {
      orderStatus = orderStatusList.find(
        (each: any) => each?.statusName.toLowerCase() === status.toLowerCase(),
      );
    }

    return (
      orderStatus || {
        statusName: status.toString(),
        orderStatus: 0,
        amber: "",
      }
    );
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortOrders = (ordersToSort: any[]) => {
    if (!sortColumn) return ordersToSort;

    return [...ordersToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "orderID":
          aValue = a.orderNumber;
          bValue = b.orderNumber;
          break;
        case "date":
          aValue = new Date(a.createdDate || a.orderDate);
          bValue = new Date(b.createdDate || b.orderDate);
          break;
        case "status":
          aValue = (a.deliveryStatus || a.status).toString().toLowerCase();
          bValue = (b.deliveryStatus || b.status).toString().toLowerCase();
          break;
        case "subtotal":
          aValue = a.amount || a.total;
          bValue = b.amount || b.total;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortableHeader = (column: string, label: string) => (
    <th className="sortable-header" onClick={() => handleSort(column)}>
      <div className="header-content">
        <span>{label}</span>
        <div className="sort-icons">
          {sortColumn === column ? (
            sortDirection === "asc" ? (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            )
          ) : (
            <>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            </>
          )}
        </div>
      </div>
    </th>
  );

  const displayAddress = customerAddressData || customer?.address;
  const addressLinePrimary = [
    displayAddress?.addressLine1 || displayAddress?.streetAddress,
    displayAddress?.addressLine2 || displayAddress?.suiteNumber,
  ]
    .filter(Boolean)
    .join(", ");
  const addressLineSecondary = [displayAddress?.postalCode]
    .filter(Boolean)
    .join(", ");

  if (initialLoading) {
    return (
      <div className="customer-details-page loading-state">
        <LoadingItem />
      </div>
    );
  }

  if (!customer) {
    return <div className="error-message">Customer not found</div>;
  }

  const status = customer?.status || "";
  const isSuspended =
    typeof status === "string" && status.toLowerCase() === "suspended";
  const totalSpentFromApiRaw =
    customerTotalSpent?.data?.totalSpent ??
    customerTotalSpent?.data?.data?.totalSpent ??
    customerTotalSpent?.data;
  const totalSpentFallback = Array.isArray(orders)
    ? orders.reduce((sum, order) => sum + (order.total || 0), 0)
    : 0;
  const totalSpent =
    typeof totalSpentFromApiRaw === "number"
      ? totalSpentFromApiRaw
      : typeof totalSpentFromApiRaw === "string"
        ? Number(totalSpentFromApiRaw) || totalSpentFallback
        : totalSpentFallback;
  const sortedOrders = sortOrders(orders);

  return (
    <div className="customer-details-page">
      <div className="customer-details-header">
        <div className="header-left">
          <button className="back-button" onClick={handleBack}>
            <FiArrowLeft />
          </button>
          <h2>
            {customer.firstName && customer.lastName
              ? `${customer.firstName} ${customer.lastName}`
              : customer.name || "Customer Details"}
          </h2>
        </div>

        <div className="header-actions">
          <button className="btn btn-edit" onClick={handleEditCustomer}>
            <FiPlus /> Edit customer's bio
          </button>
          {isSuspended ? (
            <button
              className="btn btn-activate"
              onClick={handleActivateAccount}
            >
              Activate Account
            </button>
          ) : (
            <button className="btn btn-suspend" onClick={handleSuspendAccount}>
              <FiAlertTriangle /> Suspend Account
            </button>
          )}
        </div>
      </div>

      <div className="customer-info-cards">
        <div className="info-card contact-card">
          <div className="card-header">
            <div className="card-icon">
              <img src={ContactCardIcon} alt="Contact" />
            </div>
            <h4>Contact details</h4>
          </div>
          <div className="card-content">
            <p className="card-value">{customer.phoneNumber || "N/A"}</p>
            <p className="card-subtitle">{customer.email}</p>
          </div>
        </div>

        <div className="info-card address-card">
          <div className="card-header">
            <div className="card-icon">
              <img src={AddressCardIcon} alt="Address" />
            </div>
            <h4>Last Used Address</h4>
          </div>
          <div className="card-content">
            {displayAddress && (addressLinePrimary || addressLineSecondary) ? (
              <div className="address-preview">
                <p className="card-value">
                  {addressLinePrimary}
                  {(addressLineSecondary && `, ${addressLineSecondary}`) ||
                    "N/A"}
                </p>
              </div>
            ) : (
              <p className="card-value">No address provided</p>
            )}
          </div>
        </div>

        <div className="info-card amount-card">
          <div className="card-header">
            <div className="card-icon">
              <img src={AmountCardIcon} alt="Amount" />
            </div>
            <h4>Total Amount Spent</h4>
          </div>
          <div className="card-content">
            <p className="card-value">{formatCurrency(totalSpent)}</p>
            <p className="card-subtitle">
              <span className="trend-up">
                <FiTrendingUp /> 37%
              </span>{" "}
              from last month
            </p>
          </div>
        </div>

        <div className="info-card status-card">
          <div className="card-header">
            <div className="card-icon">
              <img src={StatusCardIcon} alt="Status" />
            </div>
            <h4>Account status</h4>
          </div>
          <div className="card-content">
            <p className={`card-value status-text ${status.toLowerCase()}`}>
              {getStatusLabel(status as string)}
            </p>
          </div>
        </div>
      </div>

      <div className="orders-section">
        <div className="section-header">
          <h3>Orders</h3>
          <p className="section-subtitle">
            Manage the customer orders in one place.
          </p>
        </div>

        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                {renderSortableHeader("orderID", "Order ID")}
                {renderSortableHeader("date", "Order date")}
                {renderSortableHeader("status", "Order status")}
                {renderSortableHeader("subtotal", "Subtotal")}
                <th>Order information</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customerOrders?.loading ? (
                <tr>
                  <td colSpan={6} className="no-orders">
                    <LoadingItem />
                  </td>
                </tr>
              ) : sortedOrders.length > 0 ? (
                sortedOrders.map((order) => {
                  const statusInfo = returnDeliveryStatus(
                    order.deliveryStatus || order.status,
                  );
                  const orderDate = order.createdDate || order.orderDate;
                  const orderAmount = order.amount || order.total;
                  return (
                    <tr key={order.id}>
                      <td className="order-id">#{order.orderNumber}</td>
                      <td className="order-date">
                        {orderDate
                          ? getDateFromISO({
                              date: orderDate,
                              returnTime: true,
                            })
                          : "N/A"}
                      </td>
                      <td>
                        <span className={`order-status ${statusInfo.amber}`}>
                          {statusInfo.statusName}
                        </span>
                      </td>
                      <td className="order-total">
                        {formatCurrency(orderAmount)}
                      </td>
                      <td className="order-info">
                        <span
                          className="branded see_cart"
                          onClick={() => getOrderInfo(order.orderNumber)}
                        >
                          See cart
                        </span>
                        <span className="grayed">
                          ({order.noOfProducts || 0})
                        </span>
                      </td>
                      <td>
                        <button className="btn-more">⋮</button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="no-orders">
                    No orders found for this customer
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <CustomPagination
              pageCount={totalPages}
              currentPageNumber={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <UpdateCustomerModal
        show={showEditModal}
        onHide={handleModalHide}
        onUpdate={handleUpdateCustomer}
        customerData={customer}
        updateStatus={updateCustomerRequest}
      />

      <SuspendAccountModal
        show={showSuspendModal}
        onHide={() => setShowSuspendModal(false)}
        customerName={
          customer.firstName && customer.lastName
            ? `${customer.firstName} ${customer.lastName}`
            : customer.name || ""
        }
        onConfirm={handleConfirmSuspend}
      />

      <ActivateAccountModal
        show={showActivateModal}
        onHide={() => setShowActivateModal(false)}
        customerName={
          customer.firstName && customer.lastName
            ? `${customer.firstName} ${customer.lastName}`
            : customer.name || ""
        }
        onConfirm={handleConfirmActivate}
      />

      {[
        appConstants.GET_ORDER_DETAILS_PENDING,
        appConstants.GET_ORDER_DETAILS_SUCCESS,
        appConstants.GET_ORDER_DETAILS_FAILURE,
      ].includes(getOrderDetailsRequest?.request_status) && (
        <OrderDetailsModal
          showOrderDetailsModal
          setShowOrderDetailsModal={() => {}}
          pageProps={{
            getOrderDetailsRequest,
            getOrderDetailsAction: getOrderDetails,
          }}
          minorOrderInfo={{}}
          hideModal={hideOrderModal}
        />
      )}
    </div>
  );
};

const OrderDetailsModal = (props: any) => {
  const { getOrderDetailsRequest, getOrderDetailsAction } = props.pageProps;
  const orderInfo: any = getOrderDetailsRequest?.request_data?.response?.data;

  const hideModal = () => {
    getOrderDetailsAction("CLEAR");
  };

  return (
    <Modal
      className="order_info_wrap"
      show={props.showOrderDetailsModal}
      onHide={hideModal}
    >
      {getOrderDetailsRequest.request_status ===
        appConstants.GET_ORDER_DETAILS_SUCCESS && (
        <>
          <Modal.Header closeButton>
            <div className="order_num">Order #{orderInfo?.orderNumber}</div>
          </Modal.Header>
          <Modal.Body>
            <div className="order_headings">
              <div className="date_num">
                <div className="order_top_txt">
                  Order Date:
                  <span>
                    {getDateFromISO({
                      date: orderInfo.orderDate,
                      returnTime: true,
                    })}
                  </span>
                </div>
                <div className="order_top_txt">
                  Number of Products:{" "}
                  <span>{orderInfo?.numberOfProduct} </span>{" "}
                </div>
              </div>
            </div>
            <div className="order_full_details">
              <div className="order_items_list">
                {orderInfo?.vendorOrderDetails.map(
                  (eachItem: any, index: any) => {
                    return (
                      <div key={index} className="order_store">
                        <div className="store_info">
                          <div className="logo_n_name">
                            <div className="store_logo">
                              <img
                                src={eachItem?.storeLogoUrl}
                                className="store_logo_img"
                                alt=""
                              />
                            </div>
                            <div className="store_name">
                              {eachItem.storeName}
                              <div className="address_txt">
                                {eachItem?.storeAddress}
                              </div>
                            </div>
                          </div>
                          <div className="total_in_store">
                            ${eachItem?.amount}
                          </div>
                        </div>
                        <div className="all_products">
                          {eachItem.products.map(
                            (eachProduct: any, productIdx: any) => {
                              return (
                                <div className="each_product" key={productIdx}>
                                  <div className="item_photo_name">
                                    <div className="item_photo">
                                      <img
                                        src={eachProduct.productUrl}
                                        alt=""
                                      />
                                    </div>
                                    <div className="item_name_cta">
                                      {eachProduct?.categories && (
                                        <div className="product_cats">
                                          {eachProduct?.categories.map(
                                            (eachCat: any, catIndex: any) => {
                                              return (
                                                <span
                                                  key={catIndex}
                                                  className="each_cat_name"
                                                >
                                                  {eachCat}
                                                  {eachProduct?.categories
                                                    .length -
                                                    1 >
                                                  catIndex
                                                    ? ", "
                                                    : ""}
                                                </span>
                                              );
                                            },
                                          )}
                                        </div>
                                      )}
                                      <div className="item_name_txt">
                                        {eachProduct.name}
                                      </div>
                                      <div className="item_qty_txt">
                                        {" "}
                                        Quantity: {eachProduct.quantity}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="qty_price">
                                    <div className="qty_price_txt">
                                      ${eachProduct?.subTotal}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
              <div className="summary_wrap">
                <div className="each_box_item">
                  <div className="box_title">Order Summary</div>
                  <div className="order_main_info">
                    <div className="other_order_info">
                      <div className="each_info">
                        <div className="info_title">Subtotal</div>
                        <div className="info_price">${orderInfo?.subTotal}</div>
                      </div>
                      <div className="each_info">
                        <div className="info_title">Delivery Cost</div>
                        <div className="info_price">
                          ${orderInfo?.deliveryFees}
                        </div>
                      </div>
                      {orderInfo?.tipAmount > 0 && (
                        <div className="each_info">
                          <div className="info_title">Tip</div>
                          <div className="info_price">
                            ${orderInfo?.tipAmount}
                          </div>
                        </div>
                      )}

                      <div className="each_info">
                        <div className="info_title">Service Fee</div>
                        <div className="info_price">
                          {" "}
                          ${orderInfo?.serviceFee}
                        </div>
                      </div>
                      <div className="each_info">
                        <div className="info_title">Tax</div>

                        <div className="info_price"> ${orderInfo?.tax}</div>
                      </div>

                      <div className="each_info_main">
                        <div className="info_title"> Total</div>
                        <div className="info_price">
                          {" "}
                          ${orderInfo?.totalSum}
                        </div>
                      </div>
                      <div className="payment_mode">
                        <div className="heading">Payment Method</div>
                        <div className="payment_mode_info">
                          <div className="">
                            {orderInfo?.paymentMethodBrand === "mastercard" && (
                              <img
                                className="card_brandicons"
                                src={MasterCard}
                                alt=""
                              />
                            )}
                            {orderInfo?.paymentMethodBrand === "visa" && (
                              <img
                                src={VisaCard}
                                className="card_brandicons"
                                alt=""
                              />
                            )}
                          </div>
                          <div className="last_card_digits">
                            ****{orderInfo?.paymentCardLast4Digit}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="box_title delivery_title">
                    Delivery Information
                  </div>
                  <div className="box_title box_title_2">Delivery Type</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.isHomeDelivery === true
                          ? "Home Delivered"
                          : orderInfo?.isHomeDelivery === false
                            ? "Pick Up"
                            : "Delivery type not specified"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">
                    Leave Delivery at Door
                  </div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.leaveAtDoor === true
                          ? "Yes"
                          : orderInfo?.leaveAtDoor === false
                            ? "No"
                            : "Not specified"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">
                    Delivery Instructions
                  </div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.deliveryInstruction?.trim()?.length
                          ? orderInfo.deliveryInstruction
                          : "No special instructions"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">Shipping Address</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.shippingAddress ? (
                          <>
                            {orderInfo?.shippingAddress?.suiteNumber
                              ? `${orderInfo?.shippingAddress?.suiteNumber}, `
                              : ""}
                            {orderInfo?.shippingAddress?.addressLine1 || "N/A"}
                            <br />
                            {!orderInfo?.shippingAddress?.addressLine1 &&
                              orderInfo?.shippingAddress?.addressLine2}
                            <br />
                          </>
                        ) : (
                          <>No shipping address provided</>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">Buzzer Code</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.shippingAddress?.buzzerCode?.trim()?.length
                          ? orderInfo.shippingAddress.buzzerCode
                          : "None provided"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">
                    Expected Delivery Date & Time
                  </div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.deliveryDate
                          ? getDateFromISO({
                              date: orderInfo.deliveryDate,
                              returnTime: true,
                            })
                          : "Not scheduled"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">Customer Name</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.customerName?.trim() || "Name not provided"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">Customer Email</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.customerEmail?.trim() ||
                          "Email not provided"}
                      </div>
                    </div>
                  </div>

                  <div className="box_title box_title_2">Customer Phone</div>
                  <div className="other_order_info">
                    <div className="each_info">
                      <div className="info_title">
                        {orderInfo?.customerPhoneNumber?.trim() ||
                          "Phone number not available"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
        </>
      )}

      {getOrderDetailsRequest.request_status ===
        appConstants.GET_ORDER_DETAILS_FAILURE && (
        <Modal.Body>
          <AlertMsg
            type="error"
            message={getOrderDetailsRequest?.request_data.error}
          />
        </Modal.Body>
      )}
      {getOrderDetailsRequest.request_status ===
        appConstants.GET_ORDER_DETAILS_PENDING && (
        <Modal.Body>
          <LoadingItem />
        </Modal.Body>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  customerDetails: state.allInAppReducers.getCustomerDetailsReducer,
  customerOrders: state.allInAppReducers.getCustomerOrdersReducer,
  customerAddress: state.allInAppReducers.getACustomerAddressReducer,
  customerTotalSpent: state.allInAppReducers.getCustomerTotalSpentReducer,
  getOrderDetailsRequest: state.allInAppReducers.getOrderDetailsReducer,
  updateCustomerRequest: state.allInAppReducers.updateCustomerReducer,
});

const mapDispatchToProps = {
  getCustomerDetails: appActions.GetCustomerDetails,
  getCustomerOrders: appActions.GetCustomerOrders,
  getCustomerAddress: appActions.GetCustomerAddress,
  getCustomerTotalSpent: appActions.GetCustomerTotalSpent,
  toggleUser: appActions.ToggleUser,
  getOrderDetails: appActions.GetOrderDetails,
  updateCustomer: appActions.UpdateCustomer,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetails);
